import { AuthService } from './authService';
import type {
  Printer,
  Computer,
  CreatePrintJobRequest,
  PrintJobResponse,
  PrintJob,
  WhoAmI,
  PrintersByComputer
} from '../types/printNode';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

/**
 * Service for communicating with PrintNode API via Compass WMS backend
 */
class PrintNodeService {
  private getApiUrl(companyNo: string, endpoint: string): string {
    return `${API_BASE_URL}/${companyNo}/printnode${endpoint}`;
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }
    return response.json();
  }

  /**
   * Verify PrintNode API connection and get account information
   */
  async verifyConnection(companyNo: string): Promise<WhoAmI> {
    const response = await AuthService.authenticatedGet(
      this.getApiUrl(companyNo, '/verify')
    );
    return this.handleResponse<WhoAmI>(response);
  }

  /**
   * Get all computers with PrintNode client installed
   */
  async getComputers(companyNo: string): Promise<Computer[]> {
    const response = await AuthService.authenticatedGet(
      this.getApiUrl(companyNo, '/computers')
    );
    return this.handleResponse<Computer[]>(response);
  }

  /**
   * Get all available printers
   */
  async getPrinters(companyNo: string): Promise<Printer[]> {
    const response = await AuthService.authenticatedGet(
      this.getApiUrl(companyNo, '/printers')
    );
    return this.handleResponse<Printer[]>(response);
  }

  /**
   * Get printers for a specific computer
   */
  async getPrintersByComputer(companyNo: string, computerId: number): Promise<Printer[]> {
    const response = await AuthService.authenticatedGet(
      this.getApiUrl(companyNo, `/computers/${computerId}/printers`)
    );
    return this.handleResponse<Printer[]>(response);
  }

  /**
   * Get printers grouped by computer for UI display
   */
  async getPrintersGroupedByComputer(companyNo: string): Promise<PrintersByComputer[]> {
    const [computers, printers] = await Promise.all([
      this.getComputers(companyNo),
      this.getPrinters(companyNo)
    ]);

    // PrintNode returns computer as nested object, so we need to check computer.id
    return computers.map(computer => ({
      computer,
      printers: printers.filter(p => {
        // First try nested computer object (from PrintNode API)
        if (p.computer?.id) {
          return p.computer.id === computer.id;
        }
        // Fallback to computerId if available (from backend mapping)
        return p.computerId === computer.id;
      })
    }));
  }

  /**
   * Submit a PDF print job
   */
  async printPdf(
    companyNo: string,
    printerId: number,
    title: string,
    pdfBase64: string,
    options?: CreatePrintJobRequest['options']
  ): Promise<PrintJobResponse> {
    const request: CreatePrintJobRequest = {
      printerId,
      title,
      contentType: 'pdf_base64',
      content: pdfBase64,
      source: 'Compass WMS Web',
      options
    };

    const response = await AuthService.authenticatedPost(
      this.getApiUrl(companyNo, '/print/pdf'),
      request
    );
    return this.handleResponse<PrintJobResponse>(response);
  }

  /**
   * Submit a raw printer command print job (e.g., ZPL, ESC/POS)
   */
  async printRaw(
    companyNo: string,
    printerId: number,
    title: string,
    rawContentBase64: string,
    options?: CreatePrintJobRequest['options']
  ): Promise<PrintJobResponse> {
    const request: CreatePrintJobRequest = {
      printerId,
      title,
      contentType: 'raw_base64',
      content: rawContentBase64,
      source: 'Compass WMS Web',
      options
    };

    const response = await AuthService.authenticatedPost(
      this.getApiUrl(companyNo, '/print/raw'),
      request
    );
    return this.handleResponse<PrintJobResponse>(response);
  }

  /**
   * Get status of a specific print job
   */
  async getPrintJobStatus(companyNo: string, jobId: number): Promise<PrintJob> {
    const response = await AuthService.authenticatedGet(
      this.getApiUrl(companyNo, `/jobs/${jobId}`)
    );
    return this.handleResponse<PrintJob>(response);
  }

  /**
   * Get recent print jobs
   */
  async getRecentPrintJobs(companyNo: string, limit: number = 10): Promise<PrintJob[]> {
    const response = await AuthService.authenticatedGet(
      this.getApiUrl(companyNo, `/jobs?limit=${limit}`)
    );
    return this.handleResponse<PrintJob[]>(response);
  }

  /**
   * Helper: Convert data URL (from canvas, etc.) to base64 string without prefix
   */
  stripDataUrlPrefix(dataUrl: string): string {
    const matches = dataUrl.match(/^data:.*?;base64,(.*)$/);
    return matches ? matches[1] : dataUrl;
  }

  /**
   * Helper: Convert File to base64 string
   */
  async fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve(this.stripDataUrlPrefix(result));
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  /**
   * Helper: Print a PDF from a File object
   */
  async printPdfFile(
    companyNo: string,
    printerId: number,
    title: string,
    pdfFile: File,
    options?: CreatePrintJobRequest['options']
  ): Promise<PrintJobResponse> {
    const base64 = await this.fileToBase64(pdfFile);
    return this.printPdf(companyNo, printerId, title, base64, options);
  }

  /**
   * Print a test page to verify printer configuration
   */
  async printTestPage(
    companyNo: string,
    printerId: number,
    printerName?: string
  ): Promise<PrintJobResponse> {
    const queryParams = printerName ? `?printerName=${encodeURIComponent(printerName)}` : '';
    const response = await AuthService.authenticatedPost(
      this.getApiUrl(companyNo, `/printers/${printerId}/test${queryParams}`),
      {}
    );
    return this.handleResponse<PrintJobResponse>(response);
  }
}

export default new PrintNodeService();
